import type { NavConfig, NavItem } from '@/config/navigation/types'
import type { BreadcrumbItem } from '@/types/router'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { userCenterNavConfig } from '@/config/navigation/userCenter'
import router from '@/router' // 直接引入 router 实例，避免外部使用失去上下文

// type NavItemState = NavItem & { active?: boolean, children?: NavItemState[] }

function flattenItems(config: NavConfig): NavItem[] {
  const res: NavItem[] = []
  for (const group of config.items) {
    if (group.children) {
      for (const child of group.children) {
        res.push(child)
      }
    }
  }
  return res
}

function findItemById(config: NavConfig, id: string): NavItem | undefined {
  for (const group of config.items) {
    if (group.children) {
      for (const item of group.children) {
        if (item.id === id)
          return item
      }
    }
  }
  return undefined
}

export const useUserCenterNavigationStore = defineStore('userCenterNavigationv2', () => {
  const route = useRoute()

  // 当前激活的导航项 id（优先使用路由 meta.sidebar.activeId）
  const activeId = ref<string | null>((route.meta?.sidebar?.activeId as string | undefined) ?? null)

  const updateActiveFromRoute = () => {
    const metaActive = route.meta?.sidebar?.activeId as string | undefined
    if (metaActive) {
      activeId.value = metaActive
      return
    }
    // 兜底：尝试通过当前路由名匹配配置（若存在 routeName 与当前一致的项）
    const currentName = route.name ? String(route.name) : undefined
    if (currentName) {
      const found = flattenItems(userCenterNavConfig).find(i => i.routeName === currentName)
      if (found) {
        activeId.value = found.id
        return
      }
    }
    // 再次兜底：置空
    activeId.value = null
  }

  // 初始化与监听路由变化
  updateActiveFromRoute()
  watch(() => route.fullPath, () => updateActiveFromRoute())

  // 计算：带激活标记的导航树
  const userCenterNav = computed<NavConfig>(() => {
    const current = activeId.value
    return {
      items: userCenterNavConfig.items.map(group => ({
        ...group,
        children: group.children
          ? group.children.map(child => ({
              ...child,
              active: current ? child.id === current : false,
            }))
          : [],
      })),
    }
  })

  // 计算：面包屑（优先使用路由 meta.breadcrumbItems；否则基于分组与当前标题构造）
  // 支持 breadcrumbParent 配置：从父路由获取标题
  const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
    const metaItems = route.meta?.breadcrumbItems as BreadcrumbItem[] | undefined
    if (metaItems && Array.isArray(metaItems) && metaItems.length > 0)
      return metaItems

    const items: BreadcrumbItem[] = []
    const moduleKey = route.meta?.sidebar?.module as string | undefined
    if (moduleKey) {
      const group = userCenterNavConfig.items.find(g => g.id === moduleKey)
      if (group)
        items.push({ title: group.title, path: '/user' })
    }

    // 支持 breadcrumbParent：优先使用父路由的标题
    const breadcrumbParent = route.meta?.breadcrumbParent as string | undefined
    if (breadcrumbParent) {
      // 在路由中查找父路由的标题
      const parentRoute = router.getRoutes().find(r => r.name === breadcrumbParent)
      if (parentRoute?.meta?.title) {
        items.push({ title: String(parentRoute.meta.title), path: parentRoute.path })
      }
    }
    else if (route.meta?.title) {
      items.push({ title: String(route.meta.title) })
    }

    return items
  })

  // 行为：处理导航点击
  const handleNavClick = (id: string) => {
    const target = findItemById(userCenterNavConfig, id)
    if (!target?.routeName)
      return

    // 强制每次点击都推入目标路由
    router.push({ name: target.routeName }).catch((err) => {
      console.warn(`[navigation] router.push failed for ${target.routeName}:`, err)
    })
  }

  const updateActiveId = (id: string | null) => {
    activeId.value = id
  }

  return {
    // state
    activeId,
    // getters
    userCenterNav,
    breadcrumbItems,
    // actions
    handleNavClick,
    updateActiveId,
  }
})

export type UserCenterNavigationStore = ReturnType<typeof useUserCenterNavigationStore>
